import { Info } from 'lucide-react';
import type { OverlayTriggerState } from '@heroui/react';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';

export type UsageConfirmAction = 'cancel' | 'finish' | 'reclaim';

interface ConfirmUsageModalProps {
  state: OverlayTriggerState;
  action: UsageConfirmAction | null;
  onConfirm: () => Promise<void>;
}

const ACTION_COPY: Record<
  UsageConfirmAction,
  {
    title: string;
    question: string;
    hint: string;
    confirmLabel: string;
    cancelLabel: string;
    confirmVariant: 'primary' | 'secondary' | 'danger';
  }
> = {
  cancel: {
    title: 'Cancelar uso da vaga',
    question: 'Tem certeza que deseja cancelar o uso desta vaga?',
    hint: 'A vaga ficará disponível para uso por outro condômino após esta ação. O proprietário será notificado.',
    confirmLabel: 'Sim, cancelar',
    cancelLabel: 'Não, manter uso',
    confirmVariant: 'secondary',
  },
  finish: {
    title: 'Liberar vaga',
    question: 'Tem certeza que deseja terminar de usar esta vaga e liberá-la?',
    hint: 'Ela volta a ficar disponível para outras pessoas até o fim da janela de disponibilidade. O proprietário será notificado.',
    confirmLabel: 'Sim, liberar',
    cancelLabel: 'Não, continuar usando',
    confirmVariant: 'primary',
  },
  reclaim: {
    title: 'Retomar vaga',
    question: 'Tem certeza que deseja retomar esta vaga agora?',
    hint: 'O morador será notificado e não poderá mais usá-la. Não é necessária a aprovação dele. A vaga volta a ficar disponível até o fim da janela.',
    confirmLabel: 'Sim, retomar',
    cancelLabel: 'Não, manter empréstimo',
    confirmVariant: 'danger',
  },
};

export function ConfirmUsageModal({ state, action, onConfirm }: ConfirmUsageModalProps) {
  const copy = action ? ACTION_COPY[action] : ACTION_COPY.finish;

  return (
    <ConfirmDialog
      state={state}
      title={copy.title}
      cancelLabel={copy.cancelLabel}
      confirmLabel={copy.confirmLabel}
      confirmVariant={copy.confirmVariant}
      confirmDisabled={!action}
      onConfirm={onConfirm}
      message={
        <>
          <p className="text-muted">{copy.question}</p>
          <p className="mt-2 flex items-start gap-1 text-sm text-muted">
            <Info size={14} className="mt-0.5 shrink-0" />
            {copy.hint}
          </p>
        </>
      }
    />
  );
}
